import { useEffect, useState } from 'react'
import { supabase } from '../../services/supabase'
import { formatTRY } from '../../utils/money'

/**
 * Sistem panelinde kurumun menüsü: kategori kategori ürünler ve fiyatları.
 * Yalnız okuma. Yönetici hesabı her kurumun ürünlerini RLS'te zaten görüyor
 * (is_member, sistem yöneticisine açık).
 */
interface Row {
  id: string
  name: string
  price_kurus: number
  active: boolean
  sold_out: boolean
  by_weight: boolean
  category_id: string
  sort_order: number
}

interface Cat {
  id: string
  name: string
  sort_order: number
  active: boolean
}

export function MenuPanel({ tenantId }: { tenantId: string }) {
  const [cats, setCats] = useState<Cat[]>([])
  const [items, setItems] = useState<Row[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (!supabase) return
    let alive = true
    setLoading(true)
    setError(null)
    Promise.all([
      supabase.from('categories').select('id, name, sort_order, active').eq('tenant_id', tenantId),
      supabase
        .from('products')
        .select('id, name, price_kurus, active, sold_out, by_weight, category_id, sort_order')
        .eq('tenant_id', tenantId),
    ])
      .then(([c, p]) => {
        if (!alive) return
        if (c.error || p.error) throw new Error((c.error ?? p.error)!.message)
        setCats(((c.data ?? []) as Cat[]).sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name, 'tr')))
        setItems((p.data ?? []) as Row[])
      })
      .catch((e: Error) => alive && setError(e.message))
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [tenantId])

  const groups = cats
    .map((c) => ({
      cat: c,
      rows: items
        .filter((p) => p.category_id === c.id)
        .sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name, 'tr')),
    }))
    .filter((g) => g.rows.length > 0 || g.cat.active)

  return (
    <div className="staff-panel menu-panel">
      <button type="button" className="menu-panel__toggle" onClick={() => setOpen((v) => !v)}>
        <h4>
          Menü {loading ? '' : `(${cats.length} kategori, ${items.length} ürün)`}
        </h4>
        <span>{open ? 'Gizle' : 'Göster'}</span>
      </button>

      {error && <p className="superadmin__error">{error}</p>}

      {open && !loading && (
        groups.length === 0 ? (
          <p className="superadmin__hint">Henüz ürün eklenmemiş.</p>
        ) : (
          groups.map(({ cat, rows }) => (
            <div key={cat.id} className="menu-panel__cat">
              <div className="menu-panel__cathead">
                {cat.name}
                {!cat.active && <em>gizli</em>}
              </div>
              <ul>
                {rows.map((p) => (
                  <li key={p.id} className={!p.active ? 'is-off' : ''}>
                    <span className="menu-panel__name">
                      {p.name}
                      {!p.active && <em>gizli</em>}
                      {p.sold_out && p.active && <em className="is-out">bitti</em>}
                    </span>
                    <span className="menu-panel__price">
                      {formatTRY(p.price_kurus)}
                      {p.by_weight && <small> / kg</small>}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))
        )
      )}
    </div>
  )
}
